import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiTrash2, FiMinus, FiPlus, FiArrowLeft,
  FiShoppingBag, FiAlertTriangle, FiMessageSquare, FiRefreshCw,
} from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import API from '../utils/api';
import { formatINR } from '../utils/pricing';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────
// Stale item warning banner
// ─────────────────────────────────────────────────────────────────────
function StaleWarning({ issues }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      background: '#fff8ed', border: '1.5px solid #f59e0b',
      borderRadius: 8, padding: '10px 12px', marginTop: 8, fontSize: '0.82rem', color: '#92400e',
    }}>
      <FiAlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        {issues.map((msg, i) => <div key={i}>{msg}</div>)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Single cart item card (used on both desktop and mobile)
// ─────────────────────────────────────────────────────────────────────
function CartItem({ item, staleInfo, onRemove, onUpdateQty, updating }) {
  // unitPrice = per-piece price (e.g. ₹2.50)
  // totalPrice = unitPrice × quantity (e.g. ₹250 for 100 pcs)
  const perPiecePrice = item.isCatalog ? (parseFloat(item.unitPrice) || 0) : (parseFloat(item.price) || 0);
  const itemTotal     = item.isCatalog
    ? (parseFloat(item.totalPrice) || perPiecePrice * item.quantity)
    : perPiecePrice * item.quantity;
  const isStale   = staleInfo && !staleInfo.valid;

  const handleDecrement = () => {
    if (item.isCatalog) {
      const min = item.minimumQuantity || 1;
      const inc = item.quantityIncrement || 1;
      const next = item.quantity - inc;
      if (next < min) return;
      onUpdateQty(item.cartKey, next);
    } else {
      onUpdateQty(item.cartKey, item.quantity - 1);
    }
  };

  const handleIncrement = () => {
    if (item.isCatalog) {
      const inc = item.quantityIncrement || 1;
      onUpdateQty(item.cartKey, item.quantity + inc);
    } else {
      onUpdateQty(item.cartKey, item.quantity + 1);
    }
  };

  const isDecrementDisabled = item.isCatalog
    ? item.quantity <= (item.minimumQuantity || 1)
    : item.quantity <= 1;

  return (
    <div style={{
      border: isStale ? '1.5px solid #f59e0b' : '1px solid var(--border)',
      borderRadius: 12,
      padding: '16px',
      marginBottom: 12,
      background: isStale ? '#fffdf5' : 'var(--bg-card, #fff)',
      opacity: updating ? 0.6 : 1,
      transition: 'opacity 0.2s',
    }}>
      {/* ── Desktop layout (flex row) ── */}
      <div className="cart-item-desktop" style={{
        display: 'flex', gap: 16, alignItems: 'flex-start',
      }}>
        {/* Image */}
        <img
          src={item.image || 'https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?w=200'}
          alt={item.name}
          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
        />

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link
            to={item.isCatalog && item.categorySlug ? (item.subcategorySlug ? `/category/${item.categorySlug}/${item.subcategorySlug}/${item.slug}` : `/category/${item.categorySlug}/${item.slug}`) : `/products/${item.slug}`}
            style={{ fontWeight: 700, fontSize: '0.95rem', display: 'block', marginBottom: 3, wordBreak: 'break-word' }}
          >
            {item.name}
          </Link>

          {item.isCatalog ? (
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 2 }}>
              {item.variantName}
            </div>
          ) : (
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 2 }}>
              {item.size && <span>Size: {item.size}</span>}
              {item.size && item.color && <span> · </span>}
              {item.color && <span>Color: {item.color}</span>}
            </div>
          )}

          {/* Selected options */}
          {item.isCatalog && item.selectedOptions?.length > 0 && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 4 }}>
              {item.selectedOptions.map((o, i) => (
                <span key={i}>
                  {o.optionGroupName}: <strong>{o.optionValue}</strong>
                  {i < item.selectedOptions.length - 1 && ' · '}
                </span>
              ))}
            </div>
          )}

          {item.isCatalog && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              SKU: {item.variantSku || item.sku}
            </div>
          )}

          {/* Stale warning (desktop) */}
          {isStale && <StaleWarning issues={staleInfo.issues} />}
        </div>

        {/* Quantity controls */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            border: '1.5px solid var(--border)', borderRadius: 8, overflow: 'hidden',
          }}>
            <button
              onClick={handleDecrement}
              disabled={isDecrementDisabled || updating}
              aria-label="Decrease quantity"
              style={{
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isDecrementDisabled ? 'not-allowed' : 'pointer',
                opacity: isDecrementDisabled ? 0.4 : 1,
              }}
            >
              <FiMinus size={14} />
            </button>
            <span style={{ width: 52, textAlign: 'center', fontSize: '0.9rem', fontWeight: 600 }}>
              {item.quantity.toLocaleString('en-IN')}
            </span>
            <button
              onClick={handleIncrement}
              disabled={updating}
              aria-label="Increase quantity"
              style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <FiPlus size={14} />
            </button>
          </div>
          {item.isCatalog && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center', whiteSpace: 'nowrap' }}>
              Min {item.minimumQuantity} · Step {item.quantityIncrement}
            </div>
          )}
        </div>

        {/* Price */}
        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 80 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--primary)', fontFamily: 'var(--font-display)' }}>
            {formatINR(itemTotal)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
            ₹{perPiecePrice.toFixed(2)} / pc
          </div>
        </div>

        {/* Remove */}
        <button
          onClick={() => onRemove(item.cartKey)}
          disabled={updating}
          aria-label="Remove item"
          style={{
            color: 'var(--text-muted)', padding: 6, cursor: 'pointer', display: 'flex',
            alignItems: 'center', flexShrink: 0, borderRadius: 6,
          }}
        >
          <FiTrash2 size={17} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main CartPage
// ─────────────────────────────────────────────────────────────────────
const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, subtotal, totalItems, clearCart } = useCart();
  const navigate = useNavigate();

  const [staleMap,     setStaleMap]     = useState({}); // { cartKey: { valid, issues } }
  const [validating,   setValidating]   = useState(false);
  const [updatingKey,  setUpdatingKey]  = useState(null);

  // ── Validate catalog items on mount ─────────────────────────────
  const validateCartItems = useCallback(async () => {
    const catalogItems = cart.filter(i => i.isCatalog);
    if (catalogItems.length === 0) { setStaleMap({}); return; }

    setValidating(true);
    try {
      const payload = catalogItems.map(i => ({
        productId:       i.id,
        variantId:       i.variantId,
        selectedOptions: i.selectedOptions || [],
        quantity:        i.quantity,
      }));

      const res = await API.post('/public-quotes/validate-cart', { items: payload });
      if (res.data.success) {
        const map = {};
        res.data.results.forEach((r, idx) => {
          map[catalogItems[idx].cartKey] = { valid: r.valid, issues: r.issues || [] };
        });
        setStaleMap(map);
      }
    } catch {
      // Non-blocking — don't break the page if validation fails
    } finally {
      setValidating(false);
    }
  }, [cart]);

  useEffect(() => {
    validateCartItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // ── Handlers ────────────────────────────────────────────────────
  const handleUpdateQty = async (cartKey, newQty) => {
    setUpdatingKey(cartKey);
    updateQuantity(cartKey, newQty);
    setUpdatingKey(null);
  };

  const handleRemove = (cartKey) => {
    removeFromCart(cartKey);
    setStaleMap(prev => {
      const next = { ...prev };
      delete next[cartKey];
      return next;
    });
  };

  const hasStaleItems = Object.values(staleMap).some(v => !v.valid);

  const handleGetQuote = async () => {
    // Re-validate before navigating to get-quote
    const catalogItems = cart.filter(i => i.isCatalog);
    if (catalogItems.length > 0) {
      setValidating(true);
      try {
        const payload = catalogItems.map(i => ({
          productId: i.id, variantId: i.variantId,
          selectedOptions: i.selectedOptions || [], quantity: i.quantity,
        }));
        const res = await API.post('/public-quotes/validate-cart', { items: payload });
        if (res.data.success) {
          const map = {};
          res.data.results.forEach((r, idx) => {
            map[catalogItems[idx].cartKey] = { valid: r.valid, issues: r.issues || [] };
          });
          setStaleMap(map);
          const anyInvalid = res.data.results.some(r => !r.valid);
          if (anyInvalid) {
            toast.error('Some items have issues. Please review them before requesting a quote.');
            setValidating(false);
            return;
          }
        }
      } catch {
        toast.error('Could not validate cart. Please try again.');
        setValidating(false);
        return;
      }
      setValidating(false);
    }
    navigate('/get-quote', { state: { fromCart: true } });
  };

  // ── Empty state ──────────────────────────────────────────────────
  if (cart.length === 0) {
    return (
      <div style={{ paddingTop: 100, paddingBottom: 60 }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: 480, margin: '0 auto', padding: '60px 24px' }}>
          <div style={{ fontSize: '4rem', marginBottom: 16 }}>🛒</div>
          <h2 style={{ marginBottom: 8 }}>Your cart is empty</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 28 }}>
            Browse our products and add items to your cart.
          </p>
          <Link to="/products" className="btn btn-primary">
            <FiShoppingBag size={16} /> Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Responsive styles */}
      <style>{`
        @media (max-width: 767px) {
          .cart-page-grid { grid-template-columns: 1fr !important; }
          .cart-summary-box { position: static !important; }
          .cart-item-desktop { flex-wrap: wrap !important; }
        }
      `}</style>

      <div style={{ paddingTop: 100, paddingBottom: 60 }}>
        <div className="container">
          <h1 style={{ marginBottom: 4 }}>Shopping Cart</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 28 }}>
            {totalItems} item{totalItems !== 1 ? 's' : ''} in your cart
          </p>

          {/* Stale items global banner */}
          {hasStaleItems && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#fff8ed', border: '1.5px solid #f59e0b',
              borderRadius: 10, padding: '12px 16px', marginBottom: 20,
              fontSize: '0.88rem', color: '#92400e',
            }}>
              <FiAlertTriangle size={18} style={{ flexShrink: 0 }} />
              <span>Some items in your cart have issues. Please review them below before requesting a quote.</span>
              <button
                onClick={validateCartItems}
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', color: '#92400e', whiteSpace: 'nowrap' }}
                disabled={validating}
              >
                <FiRefreshCw size={13} /> Recheck
              </button>
            </div>
          )}

          <div
            className="cart-page-grid"
            style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}
          >
            {/* ── Cart items ── */}
            <div>
              {cart.map(item => (
                <CartItem
                  key={item.cartKey}
                  item={item}
                  staleInfo={staleMap[item.cartKey]}
                  onRemove={handleRemove}
                  onUpdateQty={handleUpdateQty}
                  updating={updatingKey === item.cartKey}
                />
              ))}

              <Link
                to="/products"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, color: 'var(--text-muted)', fontSize: '0.88rem' }}
              >
                <FiArrowLeft size={15} /> Continue Shopping
              </Link>
            </div>

            {/* ── Cart Summary ── */}
            <div
              className="cart-summary-box"
              style={{
                background: 'var(--bg-soft)', borderRadius: 'var(--radius-lg)',
                padding: 24, border: '1px solid var(--border)', position: 'sticky', top: 90,
              }}
            >
              <h3 style={{ marginBottom: 20, fontSize: '1.1rem' }}>Cart Summary</h3>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Items</span>
                <span style={{ fontWeight: 500 }}>{totalItems}</span>
              </div>

              <div style={{
                borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 8,
                display: 'flex', justifyContent: 'space-between',
                fontWeight: 800, fontSize: '1.1rem', fontFamily: 'var(--font-display)',
              }}>
                <span>Subtotal</span>
                <span style={{ color: 'var(--primary)' }}>{formatINR(subtotal)}</span>
              </div>

              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8, marginBottom: 20 }}>
                Final price and delivery charges will be confirmed in the quote.
              </p>

              <button
                onClick={handleGetQuote}
                disabled={validating || hasStaleItems}
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', fontSize: '1rem', padding: '13px', marginBottom: 0 }}
              >
                {validating
                  ? 'Validating…'
                  : <><FiMessageSquare size={16} /> Get Quote</>}
              </button>

              {hasStaleItems && (
                <p style={{ fontSize: '0.75rem', color: '#b45309', textAlign: 'center', marginTop: 8 }}>
                  Please resolve the highlighted items first.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartPage;